import React, { useState, useEffect } from 'react';
import { Layout } from './layout/Layout';
import { ModulePlaceholder } from './modules/ModulePlaceholder';
import { ClientEntity } from '../types';
import { useStore } from '../store/useStore';
import { db } from '../services/db';
import { Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

// Import our implemented modules
import { M1_CompanyData } from './modules/M1_CompanyData';
import { M2_Responsibles } from './modules/M2_Responsibles';
import { M3_BusinessDetails } from './modules/M3_BusinessDetails';
import { M4_Products } from './modules/M4_Products';
import { M5_IdealClient } from './modules/M5_IdealClient';
import { M6_Competitors } from './modules/M6_Competitors';
import { M7_Sales } from './modules/M7_Sales';
import { M12_Automations } from './modules/M12_Automations';
import { M15_Financial } from './modules/M15_Financial';
import { M16_Objectives } from './modules/M16_Objectives';
import { M17_GeneralDocs } from './modules/M17_GeneralDocs';
import { M18_KnowledgeBase } from './modules/M18_KnowledgeBase';

interface MeuNegocioDashboardProps {
  client: ClientEntity;
}

export const MeuNegocioDashboard: React.FC<MeuNegocioDashboardProps> = ({ client }) => {
  const [activeModule, setActiveModule] = useState('m1');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const store = useStore();

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const data = await db.getClientBusinessBrain(client.id);
        if (data) {
          if (data.company_data) store.setCompanyData(data.company_data);
          if (data.responsibles_data) store.setResponsibles(data.responsibles_data);
          if (data.business_details) store.setBusinessDetails(data.business_details);
          if (data.products_data) store.setProducts(data.products_data);
          if (data.ideal_client) store.setIdealClient(data.ideal_client);
          if (data.competitors_data) store.setCompetitors(data.competitors_data);
          if (data.sales_data) store.setSalesProcess(data.sales_data);
          if (data.crm_data) store.setCrmData(data.crm_data);
          if (data.automations_data) store.setAutomations(data.automations_data);
          if (data.social_proofs) store.setSocialProofs(data.social_proofs);
          if (data.financial_data) store.setFinancial(data.financial_data);
          if (data.objectives_data) store.setObjectives(data.objectives_data);
          if (data.general_docs) store.setGeneralDocs(data.general_docs);
          if (data.knowledge_base) store.setKnowledgeBase(data.knowledge_base);
        }
      } catch (err) {
        console.error('Error loading business brain:', err);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id]);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await db.saveClientBusinessBrain(client.id, store);
      toast.success('Alterações salvas com sucesso!');
    } catch (err) {
      console.error(err);
      toast.error('Erro ao salvar as informações.');
    } finally {
      setIsSaving(false);
    }
  };

  // Map module IDs to their specific components
  const renderModuleContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center p-20">
          <Loader2 className="animate-spin text-gray-400" size={32} />
        </div>
      );
    }
    
    switch (activeModule) {
      case 'm1': return <M1_CompanyData />;
      case 'm2': return <M2_Responsibles />;
      case 'm3': return <M3_BusinessDetails />;
      case 'm4': return <M4_Products />;
      case 'm5': return <M5_IdealClient />;
      case 'm6': return <M6_Competitors />;
      case 'm7': return <M7_Sales />;
      case 'm12': return <M12_Automations />;
      case 'm15': return <M15_Financial />;
      case 'm16': return <M16_Objectives />;
      case 'm17': return <M17_GeneralDocs />;
      case 'm18': return <M18_KnowledgeBase clienteId={client.id} />;
      default: return <ModulePlaceholder activeModule={activeModule} key={activeModule} />;
    }
  };

  return (
    <div className="relative">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 tracking-tight">Meu Negócio</h2>
          <p className="text-gray-500">Cérebro do Cliente - Inteligência do seu negócio</p>
        </div>
        <button
          onClick={handleSave}
          disabled={isSaving || isLoading}
          className="bg-black hover:bg-gray-900 disabled:bg-gray-400 text-white px-6 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 shadow-sm"
        >
          {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {isSaving ? 'Salvando...' : 'Salvar Alterações'}
        </button>
      </div>
      <Layout activeModule={activeModule} setActiveModule={setActiveModule}>
        <div className="pb-16 animate-in fade-in slide-in-from-bottom-4 duration-500">
          {renderModuleContent()}
        </div>
      </Layout>
    </div>
  );
};
